/**
 * VAIR 6-Player Algorithm
 *
 * Based on official VAIR 6-Player Score Card v4.15.25
 *
 * Structure:
 * - 9 total games (single round)
 * - 1 court (matches played sequentially)
 * - 2 players have byes each game
 * - Each player plays 6 games total (sits out 3 games)
 * - Some partnerships repeat (asymmetric by design)
 *
 * Note: With 6 players, perfect unique partnerships are impossible.
 * Some pairs play together twice (intentional design).
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 6 players
 * Based on official VAIR 6-Player Score Card
 */
export function generateVairSchedule6Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // Game 1: 1-6 vs 2-4 (Players 3,5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 5 }, // Players 1-6 (0-indexed: 0, 5)
        team2: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [2, 4], // Players 3, 5 (0-indexed: 2, 4)
  });
  currentTime += timePerMatch;

  // Game 2: 3-5 vs 2-6 (Players 1,4 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        team2: { player1: 1, player2: 5 }, // Players 2-6 (0-indexed: 1, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 3], // Players 1, 4 (0-indexed: 0, 3)
  });
  currentTime += timePerMatch;

  // Game 3: 4-5 vs 1-3 (Players 2,6 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1, 5], // Players 2, 6 (0-indexed: 1, 5)
  });
  currentTime += timePerMatch;

  // Game 4: 3-6 vs 1-2 (Players 4,5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 5 }, // Players 3-6 (0-indexed: 2, 5)
        team2: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [3, 4], // Players 4, 5 (0-indexed: 3, 4)
  });
  currentTime += timePerMatch;

  // Game 5: 1-5 vs 4-6 (Players 2,3 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        team2: { player1: 3, player2: 5 }, // Players 4-6 (0-indexed: 3, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1, 2], // Players 2, 3 (0-indexed: 1, 2)
  });
  currentTime += timePerMatch;

  // Game 6: 1-4 vs 2-3 (Players 5,6 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        team2: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [4, 5], // Players 5, 6 (0-indexed: 4, 5)
  });
  currentTime += timePerMatch;

  // Game 7: 4-5 vs 2-6 (Players 1,3 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        team2: { player1: 1, player2: 5 }, // Players 2-6 (0-indexed: 1, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 2], // Players 1, 3 (0-indexed: 0, 2)
  });
  currentTime += timePerMatch;

  // Game 8: 2-5 vs 1-3 (Players 4,6 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [3, 5], // Players 4, 6 (0-indexed: 3, 5)
  });
  currentTime += timePerMatch;

  // Game 9: 3-4 vs 5-6 (Players 1,2 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 4, player2: 5 }, // Players 5-6 (0-indexed: 4, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 1], // Players 1, 2 (0-indexed: 0, 1)
  });

  return { rounds };
}

/**
 * Validate 6-player VAIR schedule
 * - Each player should play in exactly 6 games (sit out 3)
 * - 2 players should have byes each game
 * - Total of 9 games
 */
export function validateVairSchedule6Players(schedule: VairSchedule): boolean {
  const playerGameCount: Map<number, number> = new Map();
  const playerByeCount: Map<number, number> = new Map();

  // Initialize
  for (let i = 0; i < 6; i++) {
    playerGameCount.set(i, 0);
    playerByeCount.set(i, 0);
  }

  // Count games and byes per player
  schedule.rounds.forEach((round) => {
    // Count active players
    round.matches.forEach((match) => {
      const allPlayers = [
        match.team1.player1,
        match.team1.player2,
        match.team2.player1,
        match.team2.player2,
      ];

      allPlayers.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
      });
    });

    // Count byes
    round.byes.forEach((player) => {
      playerByeCount.set(player, (playerByeCount.get(player) || 0) + 1);
    });
  });

  // Validate: Each player should play 6 games
  const expectedGames = 6;
  for (let i = 0; i < 6; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(
        `[6p VAIR] Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`,
      );
      return false;
    }
  }

  // Validate: Each player should have exactly 3 byes
  const expectedByes = 3;
  for (let i = 0; i < 6; i++) {
    const byes = playerByeCount.get(i) || 0;
    if (byes !== expectedByes) {
      console.error(`[6p VAIR] Player ${i + 1} had ${byes} byes, expected ${expectedByes}`);
      return false;
    }
  }

  // Validate: Should have 9 rounds total
  if (schedule.rounds.length !== 9) {
    console.error(`[6p VAIR] Expected 9 rounds, got ${schedule.rounds.length}`);
    return false;
  }

  // Validate: Each round should have exactly 1 match and 2 byes
  const invalidRounds = schedule.rounds.filter(
    (round) => round.matches.length !== 1 || round.byes.length !== 2,
  );
  if (invalidRounds.length > 0) {
    console.error(`[6p VAIR] Expected 1 match and 2 byes per round, found invalid rounds`);
    return false;
  }

  return true;
}
